import React, { useEffect, useState, useCallback, useMemo } from "react";
import moment from "moment";
import {
  ArrowUpDown,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CreditCard,
  History,
  MapPin,
  MessageSquareText,
  PackageSearch,
  Receipt,
  Tag,
  UserRound,
} from "lucide-react";
import PayPopup from "../shopCart/PayPopup";
import { GetProductsToOrder } from "../../../server/ProductToOrder";
import { orderPerCustomer } from "../../../server/Order";
import {
  getTransactionsByOrder,
  parseTransactionsResponse,
  formatSingleTransaction,
} from "../../../server/Transactions";
import { useSelector } from "react-redux";
import PopUp from "../../containers/PopUp";
import { getStatusText, getStatusClass } from "../../../utils/orderStatus";
import { formatPrice, roundMoney } from "../../../utils/formatPrice";
import {
  extractPaymentStatus,
  getPaymentStatusText,
  getPaymentStatusCssClass,
} from "../../../utils/paymentStatus";
import {
  sortUserOrders,
  USER_ORDER_SORT_OPTIONS,
  getOrderCreatedValue,
} from "../../../utils/sortUserOrders";

const INFO_ICON_SIZE = 16;
const DEFAULT_ORDER_SORT = "created_desc";
const INFO_ICON_STROKE = 2;

const formatOrderCreated = (order) => {
  const created = getOrderCreatedValue(order);
  return created && moment(created).isValid()
    ? moment(created).format("DD.MM.YY HH:mm")
    : "-";
};

const UserHistory = ({ name, setProductPopUp, setSelectedProd }) => {
  const user = useSelector((state) => state.user.user);
  const [items, setItems] = useState([]);
  const toShow = 7;
  const [currentPage, setCurrentPage] = useState(1);
  const [orderIdPay, setOrderPay] = useState(null);
  const [HaveToPay, setHaveToPay] = useState(null);
  const [paymentPopupOrderId, setPaymentPopupOrderId] = useState(null);
  const [transactionsLoadingId, setTransactionsLoadingId] = useState(null);
  const [sortBy, setSortBy] = useState(DEFAULT_ORDER_SORT);

  const PayNow = (orderId, sum) => {
    setOrderPay(orderId);
    setHaveToPay(sum);
  };

  const closePayPopup = () => {
    setOrderPay(null);
  };

  const CloseProduct = (orderId) => {
    const orders = [...items];
    const selectOrderIndex = orders.findIndex((x) => x.id === orderId);
    const selectOrder = orders[selectOrderIndex];
    selectOrder.ProductsShow = false;
    setItems(orders);
  };
  const orderIdKey = (orderId) => String(orderId);

  const setOrderTransactions = (orderId, transactions) => {
    const key = orderIdKey(orderId);
    setItems((prev) => {
      const idx = prev.findIndex((x) => orderIdKey(x.id) === key);
      if (idx === -1) return prev;
      const next = prev.map((o) => ({ ...o }));
      next[idx].transactions = transactions;
      next[idx].transactionsLoaded = true;
      return next;
    });
  };

  const fetchOrderTransactions = (orderId) => {
    const key = orderIdKey(orderId);
    const existing = items.find((x) => orderIdKey(x.id) === key);
    if (existing?.transactionsLoaded) {
      return Promise.resolve(existing.transactions ?? []);
    }

    setTransactionsLoadingId(orderId);
    return getTransactionsByOrder(orderId)
      .then((txRes) => {
        const transactions = parseTransactionsResponse(txRes.data);
        setOrderTransactions(orderId, transactions);
        return transactions;
      })
      .catch((err) => {
        console.log("error fetching transactions", err);
        setOrderTransactions(orderId, []);
        return [];
      })
      .finally(() => {
        setTransactionsLoadingId((current) =>
          orderIdKey(current) === key ? null : current,
        );
      });
  };

  const openPaymentDetails = (orderId) => {
    setPaymentPopupOrderId(orderId);
    fetchOrderTransactions(orderId);
  };

  const closePaymentPopup = () => {
    setPaymentPopupOrderId(null);
  };

  const getProductToOrder = (orderId) => {
    const key = orderIdKey(orderId);

    setItems((prev) => {
      const idx = prev.findIndex((x) => orderIdKey(x.id) === key);
      if (idx === -1) return prev;
      const next = prev.map((o) => ({ ...o }));
      const order = next[idx];
      order.ProductsShow = true;
      const coupon = order.couponDiscount || 0;
      order.finalSum = roundMoney((order.totalSum || 0) - coupon);
      return next;
    });

    const existing = items.find((x) => orderIdKey(x.id) === key);
    if (existing?.Products) return;

    GetProductsToOrder(orderId, user.StationId)
      .then((x) => {
        const orderItems = x.data?.orderItems ?? x.data ?? [];
        setItems((prev) => {
          const idx = prev.findIndex((o) => orderIdKey(o.id) === key);
          if (idx === -1) return prev;
          const next = prev.map((o) => ({ ...o }));
          const order = next[idx];
          order.Products = (Array.isArray(orderItems) ? orderItems : []).map(
            (item) => ({
              Description: item.product?.name || "מוצר לא נמצא",
              quantity: item.quantity,
              totalPrice: item.totalPrice || item.price * item.quantity,
              Price: item.price,
              ProductMkt: 0,
              Id: item.productId,
            }),
          );
          const coupon = order.couponDiscount || 0;
          order.finalSum = roundMoney((order.totalSum || 0) - coupon);
          order.ProductsShow = true;
          return next;
        });
      })
      .catch(() => console.log("error"));
  };

  const setPage = (currentPage) => {
    setCurrentPage(currentPage);
    window.scrollTo(0, 0);
  };
  const getItems = useCallback(() => {
    orderPerCustomer(user.Id, user.StationId)
      .then((x) => {
        console.log("הזמנות מהשרת:", x.data);
        const orders = sortUserOrders(
          (x.data.orders ?? []).map((o) => ({
            ...o,
            finalSum: roundMoney((o.totalSum || 0) - (o.couponDiscount || 0)),
          })),
          DEFAULT_ORDER_SORT,
        );
        setItems(orders);
      })
      .catch(() => console.log("error"));
  }, [user]);

  useEffect(() => {
    getItems();
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 200);
  }, [getItems]);

  const sortedItems = useMemo(
    () => sortUserOrders(items, sortBy),
    [items, sortBy],
  );

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * toShow;
    return sortedItems.slice(start, start + toShow);
  }, [sortedItems, currentPage, toShow]);

  let pagination = [];

  if (Array.isArray(sortedItems) && sortedItems.length > 0 && toShow > 0) {
    pagination = [...Array(Math.ceil(sortedItems.length / toShow)).keys()];
  }

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setCurrentPage(1);
  };
  const paymentPopupOrder = items.find(
    (o) =>
      paymentPopupOrderId != null &&
      orderIdKey(o.id) === orderIdKey(paymentPopupOrderId),
  );

  return (
    <div className="user-history" dir="rtl">
      {orderIdPay ? (
        <PayPopup
          closePayPopup={closePayPopup}
          orderId={orderIdPay}
          SumPay={HaveToPay}
          tranmode={false}
        />
      ) : null}
      <p className="user-history-header">
        <span className="user-history-header-icon" aria-hidden>
          <History size={24} strokeWidth={1.75} />
        </span>
        ההזמנות של {name}
      </p>
      {items.length > 0 ? (
        <div className="user-history-toolbar">
          <label className="user-history-sort" htmlFor="user-history-sort">
            <ArrowUpDown size={18} strokeWidth={2} aria-hidden />
            <span>מיון לפי</span>
            <select
              id="user-history-sort"
              value={sortBy}
              onChange={handleSortChange}
              aria-label="מיון הזמנות"
            >
              {USER_ORDER_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <span className="user-history-count">
            {sortedItems.length} הזמנות
          </span>
        </div>
      ) : null}
      <div className="orders-grid">
        {!items.length ? <h1 className="no-products">אין הזמנות</h1> : null}
        {paginatedItems.map((element) => (
          <div key={element.id} className="order-card">
            <div className="order-header">
              <div>מס' הזמנה: {element.id}</div>

              <div className="price-details">
                {element.couponDiscount ? (
                  <>
                    <p className="price original-price">
                      {(element.finalSum + element.couponDiscount).toFixed(2)}
                    </p>

                    <p className="price sale">{element.finalSum.toFixed(2)}</p>
                  </>
                ) : (
                  <p className="price">{element.finalSum.toFixed(2)}</p>
                )}
              </div>
            </div>
            <div className="order-info">
              <div className="order-info-header">
                <span
                  className={`status-badge ${getStatusClass(
                    element.tenantStatus,
                  )}`}
                >
                  {getStatusText(element.tenantStatus)}
                </span>
              </div>
              <div className="info-row">
                <span className="info-icon" aria-hidden>
                  <UserRound
                    size={INFO_ICON_SIZE}
                    strokeWidth={INFO_ICON_STROKE}
                  />
                </span>
                <span>{element.name || element.CustomerName || ""}</span>
              </div>
              <div className="info-row">
                <span className="info-icon" aria-hidden>
                  <MapPin
                    size={INFO_ICON_SIZE}
                    strokeWidth={INFO_ICON_STROKE}
                  />
                </span>
                <span className="location-text">
                  {element.address ? element.address : element.FullAddress}
                </span>
              </div>
              <div className="info-row">
                <span className="info-icon" aria-hidden>
                  <Clock3
                    size={INFO_ICON_SIZE}
                    strokeWidth={INFO_ICON_STROKE}
                  />
                </span>
                <span>{formatOrderCreated(element)}</span>
              </div>
              {element.comment && (
                <div className="info-row">
                  <span className="info-icon" aria-hidden>
                    <MessageSquareText
                      size={INFO_ICON_SIZE}
                      strokeWidth={INFO_ICON_STROKE}
                    />
                  </span>
                  <span className="comment-text">הערה: {element.comment}</span>
                </div>
              )}
              {element.couponDiscount && (
                <div className="info-row">
                  <span className="info-icon" aria-hidden>
                    <Tag size={INFO_ICON_SIZE} strokeWidth={INFO_ICON_STROKE} />
                  </span>
                  <span className="comment-text">
                    הנחת קופון: ₪{formatPrice(element.couponDiscount)}
                  </span>
                </div>
              )}
              {element.payment?.lastFourDigits && (
                <div className="info-row">
                  <span className="info-icon" aria-hidden>
                    <CreditCard
                      size={INFO_ICON_SIZE}
                      strokeWidth={INFO_ICON_STROKE}
                    />
                  </span>
                  <span>כרטיס: ****{element.payment.lastFourDigits}</span>
                </div>
              )}
              {element.PaymentDateTime && (
                <div className="info-row">
                  <span className="info-icon" aria-hidden>
                    <CalendarDays
                      size={INFO_ICON_SIZE}
                      strokeWidth={INFO_ICON_STROKE}
                    />
                  </span>
                  <span>
                    תאריך תשלום:{" "}
                    {moment(element.PaymentDateTime).format("DD.MM.YY")}
                  </span>
                </div>
              )}
            </div>
            <div
              className={`order-actions${
                element.HaveToPay && element.CanPay ? " has-pay" : ""
              }`}
            >
              {element.HaveToPay && element.CanPay ? (
                <div className="order-actions-pay">
                  <button
                    type="button"
                    className="btn btn-pay"
                    onClick={() => PayNow(element.id, element.HaveToPay)}
                  >
                    לתשלום ({formatPrice(element.HaveToPay)} ₪)
                  </button>
                </div>
              ) : null}
              <div className="order-actions-buttons">
                <button
                  type="button"
                  className="action-button view-payment"
                  disabled={
                    transactionsLoadingId != null &&
                    orderIdKey(transactionsLoadingId) === orderIdKey(element.id)
                  }
                  onClick={() => openPaymentDetails(element.id)}
                >
                  {transactionsLoadingId != null &&
                  orderIdKey(transactionsLoadingId) ===
                    orderIdKey(element.id) ? (
                    "טוען..."
                  ) : (
                    <>
                      <Receipt size={16} strokeWidth={2} aria-hidden />
                      <span>פרטי תשלום</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="action-button view-order"
                  onClick={() => getProductToOrder(element.id)}
                >
                  <PackageSearch size={16} strokeWidth={2} aria-hidden />
                  <span>הצגת הזמנה</span>
                </button>
              </div>
            </div>
            {element.ProductsShow && element && (
              <PopUp
                width="700px"
                close={() => CloseProduct(element.id)}
                className="order-popup"
              >
                <div className="modal-body">
                  <h3 className="header">פריטים בהזמנה מס׳ {element.id}:</h3>
                  <div className="order-table-container">
                    <table className="order-table">
                      <thead>
                        <tr>
                          <th>מוצר</th>
                          <th>כמות</th>
                          <th>מחיר יח'</th>
                          <th>סה"כ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {element.Products &&
                          element.Products.map((el, idx) => {
                            if (idx === 0 && el.ProductMkt === 999)
                              // delivery product
                              return null;
                            return (
                              <tr key={`${el.Id}-${idx}`}>
                                <td
                                  className="product-details"
                                  onClick={() => {
                                    setSelectedProd(el);
                                    setProductPopUp(true);
                                  }}
                                >
                                  <p className="description">
                                    {el.Description}
                                  </p>
                                </td>
                                <td className="quantity">{el.quantity}</td>
                                <td className="price">
                                  {formatPrice(el.totalPrice / el.quantity)}
                                </td>
                                <td className="total">
                                  ₪{formatPrice(el.totalPrice)}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                      <tfoot>
                        {element.couponDiscount && (
                          <tr className="delivery coupon-row">
                            <td>הנחת קופון</td>
                            <td></td>
                            <td></td>
                            <td className="coupon-price">
                              -₪{formatPrice(element.couponDiscount)}
                            </td>
                          </tr>
                        )}

                        <tr>
                          <td colSpan="2">
                            סה"כ:{" "}
                            {element.Products?.filter(
                              (p) => p.ProductMkt !== 999,
                            ).length || 0}{" "}
                            פריטים
                          </td>
                          <td />
                          <td className="final-total">
                            ₪
                            {formatPrice(
                              element.finalSum ?? element.PaymentOrg,
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="modal-actions">
                    <button
                      onClick={() => CloseProduct(element.id)}
                      className="button-secondary"
                    >
                      סגור
                    </button>
                    {/*<button
                        className="button-primary"
                        onClick={() =>
                          dispatch(CollectSiteProductSetOrder(element.id))
                        }
                      >
                        העתק לסל
                      </button>*/}
                  </div>
                </div>
              </PopUp>
            )}
          </div>
        ))}
      </div>

      {paymentPopupOrderId != null && paymentPopupOrder ? (
        <PopUp
          width="520px"
          close={closePaymentPopup}
          className="payment-popup"
        >
          <div className="modal-body payment-modal-body">
            <h3 className="payment-modal-title">
              פרטי תשלום – הזמנה {paymentPopupOrder.id}
            </h3>
            {transactionsLoadingId != null &&
            orderIdKey(transactionsLoadingId) ===
              orderIdKey(paymentPopupOrderId) ? (
              <p className="payment-loading">טוען פרטי תשלום...</p>
            ) : Array.isArray(paymentPopupOrder.transactions) &&
              paymentPopupOrder.transactions.length > 0 ? (
              <div className="payment-tx-list">
                {paymentPopupOrder.transactions.map((tx, txIdx) => {
                  const status = extractPaymentStatus(tx);
                  const digits = tx.last_four_digits ?? tx.lastFourDigits;
                  const sum = tx.sum ?? tx.amount;
                  return (
                    <div key={tx.id ?? txIdx} className="payment-tx-card">
                      {status ? (
                        <div className="payment-tx-row">
                          <span className="payment-tx-label">סטטוס</span>
                          <span
                            className={`payment-tx-value payment-tx-status payment-tx-status--${getPaymentStatusCssClass(status)}`}
                          >
                            {getPaymentStatusText(status)}
                          </span>
                        </div>
                      ) : null}
                      {sum != null ? (
                        <div className="payment-tx-row">
                          <span className="payment-tx-label">סכום</span>
                          <span className="payment-tx-value">
                            ₪{formatPrice(sum)}
                          </span>
                        </div>
                      ) : null}
                      {digits ? (
                        <div className="payment-tx-row">
                          <span className="payment-tx-label">כרטיס</span>
                          <span className="payment-tx-value">****{digits}</span>
                        </div>
                      ) : null}
                      {!status && sum == null && !digits ? (
                        <p className="payment-tx-fallback">
                          {formatSingleTransaction(tx) || "טרנזקציה"}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : paymentPopupOrder.transactionsLoaded ? (
              <p className="payment-empty">לא נמצאו רשומות תשלום להזמנה זו.</p>
            ) : null}
            <div className="modal-actions">
              <button
                type="button"
                onClick={closePaymentPopup}
                className="button-secondary"
              >
                סגור
              </button>
            </div>
          </div>
        </PopUp>
      ) : null}

      {pagination.length > 1 ? (
        <div className="pagination">
          <ul>
            {currentPage > 1 ? (
              <li
                onClick={() => setPage(currentPage - 1)}
                aria-label="עמוד קודם"
              >
                <ChevronRight size={18} strokeWidth={2} aria-hidden />
              </li>
            ) : null}

            {pagination.map((element, index) => {
              let pageN = element + 1;
              return (
                <li
                  onClick={() => setPage(pageN)}
                  key={index}
                  className={pageN === currentPage ? "active" : null}
                >
                  <span>{pageN}</span>
                </li>
              );
            })}

            {currentPage < pagination.length ? (
              <li
                onClick={() => setPage(currentPage + 1)}
                aria-label="עמוד הבא"
              >
                <ChevronLeft size={18} strokeWidth={2} aria-hidden />
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default UserHistory;
